import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, PlayCircle, Flame, ArrowLeft } from "lucide-react";

/**
 * Carnet / Curriculum public PowerKick Her — techniques Kickboxing/Muay Thaï par ceinture,
 * avec vidéos quand disponibles. Programme d'empowerment féminin (mascotte MEI).
 */
const API = `${process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "")}/api`;

const BELT_HEX = {
  Jaune: "#F5C518", Orange: "#FF8C1A", Vert: "#22C55E", Bleu: "#3B82F6",
  Rouge: "#EF4444", Mauve: "#A855F7", Brune: "#92400E", Noire: "#111827",
};

export default function PowerKickHerCarnet() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [video, setVideo] = useState(null);

  useEffect(() => {
    document.title = "Carnet PowerKick Her — Kickboxing & Muay Thaï";
    axios.get(`${API}/powerkick-her/curriculum`)
      .then(r => { setData(r.data); setOpen(r.data.belts?.[0]?.belt || null); })
      .catch(() => setData({ belts: [], total_techniques: 0 }));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="pkh-carnet-page">
      {/* En-tête */}
      <div className="relative overflow-hidden border-b border-pink-500/20 bg-gradient-to-b from-pink-950/40 to-transparent">
        <div className="max-w-4xl mx-auto px-5 py-10">
          <a href="/powerkick-her" className="inline-flex items-center gap-1.5 text-pink-300/80 hover:text-pink-200 text-sm mb-5" data-testid="pkh-back">
            <ArrowLeft size={15} /> Retour à PowerKick Her
          </a>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-11 h-11 rounded-full bg-pink-500/15 flex items-center justify-center">
              <Flame className="text-pink-400" size={22} />
            </span>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Carnet PowerKick Her</h1>
              <p className="text-pink-200/70 text-sm">Kickboxing & Muay Thaï · progression par ceinture</p>
            </div>
          </div>
          {data && (
            <p className="text-white/50 text-sm" data-testid="pkh-carnet-summary">
              {data.total_techniques} techniques · {data.belts?.length || 0} ceintures
            </p>
          )}
        </div>
      </div>

      {/* Ceintures */}
      <div className="max-w-4xl mx-auto px-5 py-8 space-y-3">
        {!data ? (
          <p className="text-white/40 text-sm">Chargement…</p>
        ) : data.belts.length === 0 ? (
          <p className="text-white/40 text-sm" data-testid="pkh-empty">Curriculum bientôt disponible.</p>
        ) : data.belts.map((b) => {
          const isOpen = open === b.belt;
          return (
            <div key={b.belt} className="border border-white/10 rounded-2xl overflow-hidden bg-white/[0.02]" data-testid={`pkh-belt-${b.belt}`}>
              <button onClick={() => setOpen(isOpen ? null : b.belt)}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/5 transition-colors"
                data-testid={`pkh-belt-toggle-${b.belt}`}>
                <span className="w-5 h-5 rounded-full border border-white/20 shrink-0" style={{ background: BELT_HEX[b.belt] || "#666" }} />
                <span className="font-bold text-left flex-1">Ceinture {b.belt}</span>
                <span className="text-xs text-white/40">{b.count}</span>
                <ChevronDown size={18} className={`text-white/40 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 space-y-1.5">
                  {b.techniques.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0" data-testid={`pkh-tech-${t.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{t.name}</div>
                        {t.category && <div className="text-[11px] uppercase tracking-wide text-white/35">{String(t.category).replace(/_/g, " ")}</div>}
                      </div>
                      {t.video_embed_url ? (
                        <button onClick={() => setVideo(t)} data-testid={`pkh-video-${t.id}`}
                          className="inline-flex items-center gap-1.5 text-pink-300 hover:text-pink-200 text-xs shrink-0">
                          <PlayCircle size={18} /> Vidéo
                        </button>
                      ) : t.video_url ? (
                        <a href={t.video_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-pink-300 hover:text-pink-200 text-xs shrink-0">
                          <PlayCircle size={18} /> Vidéo
                        </a>
                      ) : (
                        <span className="text-[11px] text-white/25 shrink-0">à venir</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Lecteur vidéo modal */}
      {video && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setVideo(null)} data-testid="pkh-video-modal">
          <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold">{video.name}</h3>
              <button onClick={() => setVideo(null)} className="text-white/60 hover:text-white text-sm">Fermer ✕</button>
            </div>
            <div className="aspect-video rounded-xl overflow-hidden border border-pink-500/20 bg-black">
              <iframe src={video.video_embed_url} title={video.name} allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                className="w-full h-full" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
